import React, { useState, useMemo } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * MultiDownloadButton
 *
 * - If there is exactly one URL whose key is "Original", we rename it
 *   to "DR Image (Raw Files)" and fire the download immediately (no menu).
 * - If there is exactly one URL but its key is not "Original", show a
 *   direct-download button labeled "Download {key}".
 * - If there are multiple URLs, render a "Download" button + dropdown menu.
 */
const MultiDownloadButton = ({ signedUrls, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleDownload = (url) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  // Normalize signedUrls: string → { Original: string }
  const normalizedUrls = useMemo(() => {
    if (typeof signedUrls === 'string') {
      return { Original: signedUrls };
    }
    return signedUrls || {};
  }, [signedUrls]);

  const downloadOptions = Object.entries(normalizedUrls); // [ [key, url], … ]

  // Display name transformer
  const displayName = (key) =>
    key.toLowerCase() === 'original' ? 'DR Image (Raw Files)' : key;

  // If there's only one URL, no dropdown
  if (downloadOptions.length === 1) {
    const [, onlyUrl] = downloadOptions[0];
    return (
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={() => handleDownload(onlyUrl)}
        disabled={disabled}
      >
        Download
      </Button>
    );
  }

  // Multiple URLs → show dropdown menu
  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<DownloadIcon />}
        onClick={handleMenuOpen}
        disabled={disabled || downloadOptions.length === 0}
      >
        Download
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {downloadOptions.map(([key, url]) => (
          <MenuItem key={key} onClick={() => handleDownload(url)}>
            {displayName(key)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
export default MultiDownloadButton;
